import type { TestCase } from './protocol';

export interface Example {
	id: string;
	name: string;
	description: string;
	source: string;
	stdin: string;
	tests: TestCase[];
}
const test = (id: string, name: string, stdin: string, expectedStdout: string): TestCase => ({
	id,
	name,
	stdin,
	expectedStdout,
	expectedExitCode: 0,
	timeoutMs: 5000,
	normalizeWhitespace: false,
});

export const examples: Example[] = [
	{
		id: 'pointers',
		name: 'Arrays & pointers',
		description:
			'Read a number, then use a pointer to change each element of an array. Global storage remains inspectable after main returns.',
		stdin: '2\n',
		source: `#include <stdio.h>

// Global storage stays in linear memory after main returns.
int numbers[4] = {1, 3, 5, 7};

int main(void) {
    int amount = 0;
    if (scanf("%d", &amount) != 1) {
        return 1;
    }

    int *cursor = numbers;
    for (int i = 0; i < 4; i++) {
        *(cursor + i) += amount;
    }

    printf("numbers: %d %d %d %d\\n",
           numbers[0], numbers[1], numbers[2], numbers[3]);
    return 0;
}
`,
		tests: [
			test('pointer-positive', 'Add two', '2\n', 'numbers: 3 5 7 9\n'),
			test('pointer-negative', 'Subtract one', '-1\n', 'numbers: 0 2 4 6\n'),
		],
	},
	{
		id: 'hello',
		name: 'Hello, C',
		description:
			'A small first program. printf writes to standard output; returning zero indicates success.',
		stdin: '',
		source: `#include <stdio.h>

int main(void) {
    printf("Hello, C!\\n");
    printf("An int is %zu bytes on this target.\\n", sizeof(int));
    return 0;
}
`,
		tests: [
			test('hello', 'Greeting and type size', '', 'Hello, C!\nAn int is 4 bytes on this target.\n'),
		],
	},
	{
		id: 'recursion',
		name: 'Recursion',
		description:
			'Each recursive call gets its own function frame. Try a number between zero and twelve.',
		stdin: '5\n',
		source: `#include <stdio.h>

unsigned int factorial(unsigned int n) {
    if (n < 2) return 1;
    return n * factorial(n - 1);
}

int main(void) {
    unsigned int n;
    if (scanf("%u", &n) != 1 || n > 12) return 1;
    printf("%u\\n", factorial(n));
    return 0;
}
`,
		tests: [
			test('factorial-five', '5 factorial', '5\n', '120\n'),
			test('factorial-zero', 'Base case', '0\n', '1\n'),
		],
	},
	{
		id: 'heap',
		name: 'Heap allocation',
		description:
			'Allocate an array, fill it, and release its storage. Freed bytes may remain visible, but no longer represent a live object.',
		stdin: '',
		source: `#include <stdio.h>
#include <stdlib.h>

int main(void) {
    int *values = malloc(4 * sizeof(*values));
    if (values == NULL) return 1;

    for (int i = 0; i < 4; i++) {
        values[i] = i * i;
        printf("%d%s", values[i], i == 3 ? "\\n" : " ");
    }

    free(values);
    return 0;
}
`,
		tests: [test('heap', 'Squares', '', '0 1 4 9\n')],
	},
];
